/**
 * AIF-01 — FE write-gating for the AI-Models management surface.
 *
 * Mirrors the AIF-01 role matrix (`.vibe/23` §6 / story MM3 + the BE
 * resolution): writes (create / patch / delete model·version·class + import)
 * need `tenant_admin`, `expert_analyst` or `platform_admin`; reads (list /
 * detail) additionally allow `pre_screener`. The route itself carries no gate
 * — READ is open — so only the write affordances below are hidden for a
 * read-only persona (AC4).
 *
 * The backend is the security boundary (403 on a write without the role; the
 * §3.1 guard 409s a class re-index). This hook is the UX nicety so a
 * read-only user never sees a control the server will refuse.
 *
 * Role IDENTITY is the lever (not a permission-matrix key): the BE gates via
 * `hasRole(...)`, so we use the sanctioned `useHasRole` escape hatch (see
 * `packages/auth/src/useCurrentUserRole.ts`).
 */
import { useHasRole } from "@aeriosiq/auth-client";

export interface AiModelWritePermissions {
  /** Create / edit / delete models, versions, classes; import classes. */
  canWrite: boolean;
}

export function useAiModelWritePermissions(): AiModelWritePermissions {
  const isTenantAdmin = useHasRole("tenant_admin");
  const isExpertAnalyst = useHasRole("expert_analyst");
  const isPlatformAdmin = useHasRole("platform_admin");
  return { canWrite: isTenantAdmin || isExpertAnalyst || isPlatformAdmin };
}
